import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'

/**
 * Catálogo de Notificaciones (`/notifications`). Namespace `shell.notifications.*`:
 * es una pantalla transversal del armazón, no de un módulo de negocio.
 *
 * No se traducen: el asunto ni el cuerpo de una notificación (los genera el
 * backend) ni los mensajes de error que llegan de la API, que van dentro de
 * `common.error.withMessage`.
 */
export const NOTIFICATION_MESSAGES = defineMessages({
  // ── Página ──
  title: {id: 'shell.notifications.title', defaultMessage: 'Notifications'},
  home: {id: 'shell.notifications.home', defaultMessage: 'Home'},
  categories: {id: 'shell.notifications.categories', defaultMessage: 'Categories'},
  searchPlaceholder: {
    id: 'shell.notifications.search',
    defaultMessage: 'Search subject or message',
  },
  // El menu de la campana ya usa `tab.*`, pero alli la etiqueta lleva el
  // contador dentro (`All ({count})`). Aqui va suelta, asi que son llaves aparte.
  // No reusa `common.pagination.all`: alli el ES es «Todos» y aqui concuerda
  // con «notificaciones», femenino.
  tabAll: {id: 'shell.notifications.filter.all', defaultMessage: 'All'},
  tabUnread: {id: 'shell.notifications.filter.unread', defaultMessage: 'Unread'},
  tabRead: {id: 'shell.notifications.filter.read', defaultMessage: 'Read'},
  empty: {id: 'shell.notifications.empty', defaultMessage: 'No notifications found'},
  badgeNew: {id: 'shell.notifications.badge.new', defaultMessage: 'New'},

  // ── Acciones en bloque ──
  selectedCount: {
    id: 'shell.notifications.selectedCount',
    defaultMessage: '{count, plural, one {# selected} other {# selected}}',
  },
  markAsRead: {id: 'shell.notifications.action.markRead', defaultMessage: 'Mark as Read'},
  markAsUnread: {id: 'shell.notifications.action.markUnread', defaultMessage: 'Mark as Unread'},

  // ── Columnas ──
  colSubject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  colPriority: {id: 'shell.notifications.col.priority', defaultMessage: 'Priority'},
  colRead: {id: 'shell.notifications.col.read', defaultMessage: 'Read'},

  // ── Acciones de fila ──
  openDetails: {
    id: 'shell.notifications.action.openDetails',
    defaultMessage: 'Open details (right click to open in a new tab/window)',
  },
  goToDetails: {
    id: 'shell.notifications.action.goToDetails',
    defaultMessage: 'Go to details (new tab)',
  },
  approveAssignment: {
    id: 'shell.notifications.action.approveAssignment',
    defaultMessage: 'Approve assignment',
  },
  approveCampaign: {
    id: 'shell.notifications.action.approveCampaign',
    defaultMessage: 'Approve and launch campaign',
  },
  callBack: {id: 'shell.notifications.voicemail.callBack', defaultMessage: 'Call back'},
  alreadyReturnedBy: {
    id: 'shell.notifications.missedCall.alreadyReturnedBy',
    defaultMessage: 'Already returned by {name}',
  },

  // ── Toasts y confirmaciones ──
  errLoading: {
    id: 'shell.notifications.toast.errorLoading',
    defaultMessage: 'Error loading notifications: {message}',
  },
  errMarkingRead: {
    id: 'shell.notifications.toast.errorMarkingRead',
    defaultMessage: 'Error marking notification as read: {message}',
  },
  errNotAssignmentRequest: {
    id: 'shell.notifications.error.notAssignmentRequest',
    defaultMessage: 'This notification is not a policy assignment request',
  },
  errMissingAssignmentData: {
    id: 'shell.notifications.error.missingAssignmentData',
    defaultMessage:
      'Missing required data to approve assignment. PolicyId: {policyId}, RequesterId: {requesterId}',
  },
  toastAssignmentApproved: {
    id: 'shell.notifications.toast.assignmentApproved',
    defaultMessage: 'Policy assignment approved successfully',
  },
  errApprovingAssignment: {
    id: 'shell.notifications.error.approvingAssignment',
    defaultMessage: 'Error approving assignment: {message}',
  },
  errMissingCampaignData: {
    id: 'shell.notifications.error.missingCampaignData',
    defaultMessage: 'Missing campaign data in notification',
  },
  toastCampaignApproved: {
    id: 'shell.notifications.toast.campaignApproved',
    defaultMessage: 'Campaign approved and launched',
  },
  errLaunchCampaign: {
    id: 'shell.notifications.error.launchCampaign',
    defaultMessage: 'Failed to launch campaign',
  },
  errApprovingCampaign: {
    id: 'shell.notifications.error.approvingCampaign',
    defaultMessage: 'Error approving campaign launch: {message}',
  },
  warnSelectFirst: {
    id: 'shell.notifications.warn.selectFirst',
    defaultMessage: 'Please select notifications first',
  },
  toastMarkedRead: {
    id: 'shell.notifications.toast.markedRead',
    defaultMessage:
      '{count, plural, one {# notification marked as read} other {# notifications marked as read}}',
  },
  errMarkingSelectedRead: {
    id: 'shell.notifications.error.markingSelectedRead',
    defaultMessage: 'Error marking notifications as read: {message}',
  },
  toastMarkedUnread: {
    id: 'shell.notifications.toast.markedUnread',
    defaultMessage:
      '{count, plural, one {# notification marked as unread} other {# notifications marked as unread}}',
  },
  errMarkingSelectedUnread: {
    id: 'shell.notifications.error.markingSelectedUnread',
    defaultMessage: 'Error marking notifications as unread: {message}',
  },
  confirmDeleteSelected: {
    id: 'shell.notifications.confirm.deleteSelected',
    defaultMessage:
      'Are you sure you want to delete {count, plural, one {# notification} other {# notifications}}?',
  },
  toastDeletedSelected: {
    id: 'shell.notifications.toast.deletedSelected',
    defaultMessage:
      '{count, plural, one {# notification deleted} other {# notifications deleted}}',
  },
  errDeleting: {
    id: 'shell.notifications.error.deleting',
    defaultMessage: 'Error deleting notifications: {message}',
  },
  toastMarkedUnreadOne: {
    id: 'shell.notifications.toast.markedUnreadOne',
    defaultMessage: 'Notification marked as unread',
  },
  confirmDeleteOne: {
    id: 'shell.notifications.confirm.deleteOne',
    defaultMessage: 'Are you sure you want to delete this notification?',
  },
  toastDeletedOne: {
    id: 'shell.notifications.toast.deleted',
    defaultMessage: 'Notification deleted',
  },

  // ── Reusadas ──
  type: {id: 'common.field.type', defaultMessage: 'Type'},
  /** `Date` estaba repetido, feature-scoped, en cuatro namespaces: va a `common`. */
  date: {id: 'common.field.date', defaultMessage: 'Date'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
})

/** Guion largo de celda vacía. Símbolo, no texto. */
export const EM_DASH = '—'

type Labels = Record<string, MessageDescriptor>

/**
 * Prioridad de la notificación. El código viaja de la API; la etiqueta se
 * traduce. Las llaves ya existían en el catálogo del menú de la campana
 * (Fase 1.8) pero en MAYÚSCULAS; se unifican en capitalizado, que es el criterio
 * del resto de insignias. El español no cambia.
 */
const PRIORITY: Labels = defineMessages({
  URGENT: {id: 'shell.notifications.priority.urgent', defaultMessage: 'Urgent'},
  HIGH: {id: 'shell.notifications.priority.high', defaultMessage: 'High'},
  NORMAL: {id: 'shell.notifications.priority.normal', defaultMessage: 'Normal'},
  LOW: {id: 'shell.notifications.priority.low', defaultMessage: 'Low'},
}) as Labels

/**
 * Tipo de notificación, en **singular**: es la insignia de cada fila de la tabla.
 * El listado lateral usa el plural (`typeCategoryLabel`) porque ahí son
 * categorías que agrupan varias.
 */
const TYPE_BADGE: Labels = defineMessages({
  POLICY_NOTE_MENTION: {id: 'shell.notifications.type.policyMention', defaultMessage: 'Policy Mention'},
  POLICY_STATUS_CHANGE: {id: 'shell.notifications.type.policyStatus', defaultMessage: 'Policy Status'},
  POLICY_ASSIGNMENT: {
    id: 'shell.notifications.type.policyAssignment',
    defaultMessage: 'Policy Assignment',
  },
  POLICY_ASSIGNMENT_REQUEST: {
    id: 'shell.notifications.type.assignmentRequest',
    defaultMessage: 'Assignment Request',
  },
  POLICY_PAYMENT_VIEW: {id: 'shell.notifications.type.paymentView', defaultMessage: 'Payment View'},
  COMMISSION_CLAIM: {
    id: 'shell.notifications.type.commissionClaim',
    defaultMessage: 'Commission Claim',
  },
  COMMISSION_APPROVED: {
    id: 'shell.notifications.type.commissionApproved',
    defaultMessage: 'Commission Approved',
  },
  COMMISSION_REJECTED: {
    id: 'shell.notifications.type.commissionRejected',
    defaultMessage: 'Commission Rejected',
  },
  POLICY_REMINDER: {id: 'shell.notifications.type.reminder', defaultMessage: 'Reminder'},
  LEAD_ASSIGNMENT: {id: 'shell.notifications.type.leadAssignment', defaultMessage: 'Lead Assignment'},
  TICKET_ASSIGNED: {id: 'shell.notifications.type.ticketAssigned', defaultMessage: 'Ticket Assigned'},
  TICKET_STATUS_CHANGE: {id: 'shell.notifications.type.ticketStatus', defaultMessage: 'Ticket Status'},
  TICKET_COMMENT: {id: 'shell.notifications.type.ticketComment', defaultMessage: 'Ticket Comment'},
  SIGNATURE_COMPLETED: {
    id: 'shell.notifications.type.signatureCompleted',
    defaultMessage: 'Signature Completed',
  },
  SIGNATURE_PENDING: {
    id: 'shell.notifications.type.signaturePending',
    defaultMessage: 'Signature Pending',
  },
  CAMPAIGN_LAUNCH_REQUEST: {
    id: 'shell.notifications.type.campaignApproval',
    defaultMessage: 'Campaign Approval',
  },
  VOICEMAIL: {id: 'shell.notifications.type.voicemail', defaultMessage: 'Voicemail'},
  MISSED_CALL: {id: 'shell.notifications.type.missedCall', defaultMessage: 'Missed Call'},
  GENERAL: {id: 'common.value.general', defaultMessage: 'General'},
}) as Labels

/** El mismo tipo, en **plural**: son las categorías del panel lateral. */
const TYPE_CATEGORY: Labels = defineMessages({
  ALL: {id: 'shell.notifications.category.all', defaultMessage: 'All Notifications'},
  POLICY_NOTE_MENTION: {
    id: 'shell.notifications.category.policyMentions',
    defaultMessage: 'Policy Mentions',
  },
  POLICY_ASSIGNMENT: {
    id: 'shell.notifications.category.policyAssignments',
    defaultMessage: 'Policy Assignments',
  },
  POLICY_ASSIGNMENT_REQUEST: {
    id: 'shell.notifications.category.assignmentRequests',
    defaultMessage: 'Assignment Requests',
  },
  POLICY_PAYMENT_VIEW: {
    id: 'shell.notifications.category.paymentViews',
    defaultMessage: 'Payment Views',
  },
  COMMISSION_CLAIM: {
    id: 'shell.notifications.category.commissionClaims',
    defaultMessage: 'Commission Claims',
  },
  POLICY_REMINDER: {id: 'shell.notifications.category.reminders', defaultMessage: 'Reminders'},
  LEAD_ASSIGNMENT: {
    id: 'shell.notifications.category.leadAssignments',
    defaultMessage: 'Lead Assignments',
  },
  TICKET_ASSIGNED: {
    id: 'shell.notifications.category.ticketsAssigned',
    defaultMessage: 'Tickets Assigned',
  },
  TICKET_COMMENT: {
    id: 'shell.notifications.category.ticketComments',
    defaultMessage: 'Ticket Comments',
  },
  CAMPAIGN_LAUNCH_REQUEST: {
    id: 'shell.notifications.category.campaignApprovals',
    defaultMessage: 'Campaign Approvals',
  },
  VOICEMAIL: {id: 'shell.notifications.category.voicemails', defaultMessage: 'Voicemails'},
  MISSED_CALL: {
    id: 'shell.notifications.category.missedCalls',
    defaultMessage: 'Missed Calls',
  },
}) as Labels

const label = (intl: IntlShape, map: Labels, key?: string | null) =>
  key && map[key] ? intl.formatMessage(map[key]) : key ?? ''

/** Etiqueta de prioridad. Devuelve `''` si el código no se reconoce. */
export const priorityLabel = (intl: IntlShape, value?: string | null) =>
  value && PRIORITY[value] ? intl.formatMessage(PRIORITY[value]) : ''

/** Etiqueta de tipo para la insignia de la tabla (singular). */
export const typeBadgeLabel = (intl: IntlShape, value?: string | null) =>
  label(intl, TYPE_BADGE, value)

/**
 * Etiqueta de tipo para el panel de categorías (plural). Los tipos que dicen lo
 * mismo en singular y en plural caen al mapa de insignias en vez de duplicar la
 * llave (`Policy Status`, `Commission Approved`, `Signature Pending`…).
 */
export const typeCategoryLabel = (intl: IntlShape, value?: string | null) =>
  value && TYPE_CATEGORY[value]
    ? intl.formatMessage(TYPE_CATEGORY[value])
    : label(intl, TYPE_BADGE, value)
